import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { apiErrorMessage } from '../../../utils/apiError';
import { UTC_INPUT_HINT, utcInputFromNow, utcInputToIso } from '../../../utils/utcDateTime';

// What the SOL limit counts (waitlist cap + expiry, 2026-09-17):
//   played    — today's shop behaviour: SOL actually played through RFPs.
//   allocated — waitlist: SOL of every RFP minted (claims), first come first
//               served, so the cap is what the /vip card counts down.
// The default sends NOTHING (aegis' server default is `played`), so shop
// campaigns keep working against an aegis that has not shipped capMode yet.
export const CapMode = {
  PLAYED: 'played',
  ALLOCATED: 'allocated',
};

export const buildCampaignBody = ({ name, expireAt, solLimit, capMode }) => ({
  name,
  expireAt,
  solLimit,
  ...(capMode === CapMode.ALLOCATED && { capMode }),
});

const CreateCampaignModal = ({ show, onHide, onCreate }) => {
  const [name, setName] = useState('');
  const [expireAt, setExpireAt] = useState(() => utcInputFromNow({ weeks: 2 }));
  const [solLimit, setSolLimit] = useState('');
  const [capMode, setCapMode] = useState(CapMode.PLAYED);

  const handleSubmit = async () => {
    let parsedSolLimit = null;
    const trimmedValue = solLimit.trim();

    if (trimmedValue !== '') {
      const numericValue = parseFloat(trimmedValue);

      if (Number.isNaN(numericValue) || numericValue < 0) {
        alert('SOL limit must be a non-negative number.');
        return;
      }

      parsedSolLimit = numericValue;
    }

    // A claims cap with no limit caps nothing: the /vip card would count
    // down from "unlimited". Require a positive limit for waitlist campaigns.
    if (capMode === CapMode.ALLOCATED && !(parsedSolLimit > 0)) {
      alert('A campaign that counts claims needs a SOL limit above 0.');
      return;
    }

    const expireAtIso = utcInputToIso(expireAt);
    if (!expireAtIso) {
      alert('Expiry must be a valid date.');
      return;
    }

    try {
      await onCreate(
        buildCampaignBody({ name, expireAt: expireAtIso, solLimit: parsedSolLimit, capMode })
      );
    } catch (error) {
      // aegis' refusal (a 400 on capMode before it ships, an expiry in the
      // past) used to vanish into an unhandled rejection with the modal open.
      console.error('Error creating campaign:', error);
      alert(apiErrorMessage(error, 'Could not create the campaign. Please try again.'));
      return;
    }
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Create Campaign</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Campaign Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter campaign name"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Expiration Date</Form.Label>
            <Form.Control
              type="datetime-local"
              value={expireAt}
              onChange={e => setExpireAt(e.target.value)}
            />
            <Form.Text muted>{UTC_INPUT_HINT}</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>SOL Limit (optional)</Form.Label>
            <Form.Control
              type="number"
              placeholder="Leave empty for unlimited"
              value={solLimit}
              onChange={e => setSolLimit(e.target.value)}
              min="0"
              step="0.01"
            />
            <Form.Text className="text-muted">
              Maximum total SOL that can be redeemed from this campaign
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Cap counts</Form.Label>
            <div>
              <Form.Check
                type="radio"
                id="capModePlayed"
                label="plays (shop)"
                name="capMode"
                value={CapMode.PLAYED}
                checked={capMode === CapMode.PLAYED}
                onChange={e => setCapMode(e.target.value)}
              />
              <Form.Check
                type="radio"
                id="capModeAllocated"
                label="claims (waitlist)"
                name="capMode"
                value={CapMode.ALLOCATED}
                checked={capMode === CapMode.ALLOCATED}
                onChange={e => setCapMode(e.target.value)}
              />
            </div>
            <Form.Text className="text-muted">
              Plays: the limit counts SOL actually played (today's shop behaviour). Claims: the
              limit counts every risk-free play minted, first come first served — pick this for a
              waitlist wave so the /vip card can count the budget down.
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          Create Campaign
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateCampaignModal;
